import { useEffect, useState } from 'react';
import { useApi, useSession } from '../auth/session';
import { prefsStore, readCache, usePrefsValue } from './prefsStore';
import { PrefsSyncer } from './prefsSync';

const USER_KEY = 'adiox.prefs.user';

/**
 * The cached preferences belong to one user. When somebody else signs in on this browser the cache is dropped, so one
 * person's layout and watch list never show up for the next.
 */
export function bindCacheToUser(
  userId: string | undefined,
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
): void {
  if (!userId) return;
  let cachedUser: string | null = null;
  try {
    cachedUser = storage.getItem(USER_KEY);
    if (cachedUser !== userId) storage.setItem(USER_KEY, userId);
  } catch {
    // storage blocked: nothing is cached anyway
  }
  if (cachedUser !== null && cachedUser !== userId) {
    prefsStore.setState({ data: {}, revision: 0, dirty: [], status: 'local' });
    try {
      localStorage.removeItem('adiox.prefs.v2');
    } catch {
      // ignore
    }
  } else if (cachedUser === null) {
    prefsStore.setState({ data: readCache() }); // first run with a user: keep what was set before signing in
  }
}

/** Loads the user's preferences from the server and keeps them saved. Returns true once the first load finished. */
export function usePrefsSync(): boolean {
  const api = useApi();
  const userId = useSession().user?.id;
  const [started, setStarted] = useState(false);
  const status = usePrefsValue((s) => s.status);

  useEffect(() => {
    bindCacheToUser(userId);
    const syncer = new PrefsSyncer(prefsStore, api);
    let cancelled = false;
    void syncer.start().finally(() => !cancelled && setStarted(true));
    const onHide = () => {
      if (document.visibilityState === 'hidden') void syncer.flush();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onHide);
      void syncer.flush().finally(() => syncer.stop());
    };
  }, [api, userId]);

  return started && status !== 'loading';
}
